import type { QueryKey } from '@tanstack/react-query';
import { userHasAnyPermission } from '@/shared/constants/leaveSchedulingPermissions';
import { MASTER_DATA_QUERY_PREFIX, MASTER_DATA_RESOURCES, type MasterDataResource } from './masterDataResources';

/**
 * GET permissions for each catalog. Any one match allows the read, same as
 * `checkAnyPermission` on the route. Keep in sync with those handlers.
 */
export const MASTER_DATA_READ_PERMISSIONS: Record<MasterDataResource, readonly string[]> = {
  'appointment.types': [
    'appointment-types:read',
    'appointments:read',
    'appointments:create',
    'appointments:update',
  ],
  'treatment-catalog.list': ['treatment-catalog:read'],
  'treatment-catalog.categories': [
    'treatment-catalog:read',
    'treatment-catalog:create',
    'treatment-catalog:update',
  ],
  'treatment-catalog.uoms': [
    'treatment-catalog:read',
    'treatment-catalog:create',
    'treatment-catalog:update',
  ],
  'treatment-journal.list': [
    'treatment-journal:read',
    'treatment-journal:create',
    'treatment-journal:update',
    'treatment-session:read',
    'treatment-session:create',
    'treatment-session:update',
  ],
  'clinic.chairs': [
    'clinics:read',
    'clinics:manage',
    'appointments:read',
    'appointments:create',
    'appointments:update',
  ],
  'patient.customer-sources': [
    'customer-sources:read',
    'patients:read',
    'patients:create',
    'patients:update',
    'leads:read',
    'leads:create',
    'leads:update',
  ],
  'staff.departments': [
    'departments:read',
    'staff:read',
    'staff:create',
    'staff:update',
    'scheduling:read',
    'leave:read',
    'leave:request',
  ],
  'staff.roster': ['staff:read'],
  'expense.categories': ['expense:read'],
  'income.categories': ['income:read'],
  'payment.methods': [
    'payment-methods:read',
    'payment:read',
    'payment:create',
    'payment:update',
    'payment:refund',
    'income:read',
    'income:create',
    'expense:read',
    'expense:create',
    'finance:read',
    'wallet:read',
    'wallet:topup',
    'wallet:refund',
    'commission:read',
    'labo:read',
    'labo:update',
  ],
  'prescription.drugs': ['prescriptions:read'],
  'prescription.templates': ['prescriptions:read'],
  'scheduling.shift-types': ['scheduling:read'],
  'scheduling.time-off-types': ['leave:read'],
  'overtime.types': [
    'overtime:read',
    'overtime:request',
    'overtime:approve',
    'overtime:manage',
    'overtime:types:manage',
  ],
  'labo.work-types': ['labo:read', 'labo:create', 'labo:update'],
  'labo.materials': ['labo:read', 'labo:create', 'labo:update'],
  'labo.shades': ['labo:read', 'labo:create', 'labo:update'],
  'labo.partners': ['labo:read'],
  'messaging.canned-responses': ['messaging:read'],
  'payroll.components': ['payroll:read'],
  'kpi.templates': ['kpi:read'],
  'inventory.taxonomy': ['inventory:read'],
};

const keyStartsWith = (queryKey: readonly unknown[], prefix: readonly string[]): boolean =>
  prefix.every((part, index) => queryKey[index] === part);

export const readPermissionsForMasterQuery = (queryKey: QueryKey): readonly string[] | null => {
  const key = queryKey as readonly unknown[];
  for (const resource of MASTER_DATA_RESOURCES) {
    if (!keyStartsWith(key, MASTER_DATA_QUERY_PREFIX[resource])) continue;
    return MASTER_DATA_READ_PERMISSIONS[resource];
  }
  return null;
};

/** Same rule as `usePermission`: org admin bypasses the list. */
export const canReadMasterCatalog = (
  role: string | undefined,
  permissions: string[] | undefined,
  required: readonly string[]
): boolean => {
  if (role === 'admin') return true;
  return userHasAnyPermission(permissions, [...required]);
};
